










































































































































































































































































import moment from 'moment';
import * as R from 'ramda';
import Vue from 'vue';
import { Decimal } from 'decimal.js';

import { Entry as EntryEntity } from '@/core/entity';
import {
  getCategory,
  getTasks,
  GqlAttachment,
  GqlAuditor,
  GqlCategory,
  GqlEntry,
  GqlTask,
} from './graphql';
import { Entry } from './types';

import MoreInfoView from './MoreInfoView.vue';
import PhotoGallery from './PhotoGallery.vue';
import TableHeader from './TableHeader.vue';
import Product from './Product.vue';
import PricePanel, { SHOW_PRICES } from './PricePanel.vue';

interface DocumentWithVendorExts extends Document {
  webkitFullscreenEnabled?: boolean;
  mozFullScreenEnabled?: boolean;
  msFullscreenEnabled?: boolean;

  mozCancelFullScreen: () => void;
  msExitFullscreen: () => void;
  webkitExitFullscreen: () => void;
}

interface DocumentElementWithVendorExts extends HTMLElement {
  mozRequestFullScreen: () => void;
  msRequestFullscreen: () => void;
  webkitRequestFullscreen: () => void;
}

interface Outlet {
  auditor: {
    id: string;
    name: string;
    avatar: string | null;
  } | null;
  outlet: {
    id: string;
    name: string;
    logo: string;
  };
}

interface Product {
  id: string;
  name: string;
  photo: string | null;
  outlets: { [id: string]: Entry };
}

export default Vue.extend({
  components: {
    'table-header': TableHeader,
    'photo-gallery': PhotoGallery,
    product: Product,
    'price-panel': PricePanel,
    'view-more': MoreInfoView,
  },
  props: {
    filter: {
      type: Object,
      required: false,
    },
  },
  apollo: {
    category: {
      query: getCategory,
      variables() {
        const category = this.$route.params.category;
        return { id: category };
      },
      skip() {
        const category = this.$route.params.category;
        return category === 'high-priority';
      },
    },
    tasks: {
      query: getTasks,
      variables() {
        const date = this.$route.params.date;
        return {
          date,
          jobIds: this.filter.jobIds,
          outletIds: this.filter.outletIds,
        };
      },
      fetchPolicy: 'network-only',
    },
  },

  data() {
    return {
      busy: false,
      category: null as GqlCategory | null,
      isApproved: false,
      // entry id or null
      moreInfoId: null as { entryId: string; outletId: string } | null,
      photo: null as string | null,
      // entry id or null
      photoGalleryId: null as string | null,
      showPrice: 0,
      tasks: null as GqlTask[] | null,
      isFullscreen: false,
    };
  },

  computed: {
    auditor(): GqlAuditor | null {
      if (this.moreInfoId === null) {
        return null;
      }

      const { entryId } = this.moreInfoId;
      const entry = R.find(R.propEq('id', entryId))(this.entries);

      return entry ? entry.auditor : null;
    },

    categoryId(): string | null {
      const category = this.$route.params.category;
      return category === 'high-priority' ? 'high-priority' : category;
    },

    categoryName(): string {
      const category = this.$route.params.category;
      // Changes to temp hide selection of high priority / category
      // if (category === 'high-priority') {
      //   return 'High Priority';
      // }

      return this.category ? this.category.name : '';
    },

    entries(): Entry[] {
      const { category } = this;
      const categoryName = category ? `Intrack / ${this.category!.name}` : '';
      return R.pipe(
        R.map<GqlTask, Entry[]>((t) => {
          // Changes to temp hide selection of high priority / category
          // get all entries matching category
          // const entries = category
          //   ? R.filter(
          //       (e) =>
          //         e.product.category!.canonicalName.startsWith(categoryName),
          //       t.entries,
          //     )
          //   : t.entries;
          const entries = t.entries;

          return R.map(
            (e) => ({ ...e, auditor: t.auditor, outletId: t.outlet.id }),
            entries,
          );
        }),
        R.unnest,
      )(this.filteredTasks);
    },

    fieldPhotos(): GqlAttachment[] {
      const entryId = this.photoGalleryId;
      if (entryId === null) {
        return [];
      }

      const entry = R.find(R.propEq('id', entryId))(this.entries);
      return entry ? entry.attachments : [];
    },

    filteredTasks(): GqlTask[] {
      if (this.tasks === null) {
        return [];
      }

      const { categoryId } = this;
      // Changes to temp hide selection of high priority / category
      // return R.pipe(
      //   R.ifElse(
      //     () => categoryId === 'high-priority',
      //     R.filter<GqlTask, 'array'>((t) => t.priority === 10),
      //     R.filter<GqlTask, 'array'>((t) => t.priority === 1),
      //   ),
      // )(this.tasks);
      return this.tasks;
    },

    outlets(): Outlet[] {
      return R.pipe(
        R.map<GqlTask, Outlet>((t) => {
          const a = t.auditor;

          return {
            auditor: a
              ? {
                  id: a.id,
                  name: a.name,
                  avatar: a.avatar ? a.avatar.url : null,
                }
              : null,
            outlet: {
              id: t.outlet.id,
              name: t.outlet.name,
              logo: t.outlet.retailer.logo.url,
            },
          };
        }),
        R.sortWith([R.ascend((r) => r.outlet.name)]),
      )(this.filteredTasks);
    },

    products(): Product[] {
      const result = R.pipe(
        R.groupBy<Entry>((e) => e.product.id),
        R.values,
        R.map<Entry[], Product>((list) => {
          const f = R.head(list)!;
          const outlets = R.reduce(
            (acc, e) => {
              const key = `${e.outletId}.${e.auditor ? e.auditor!.id : ''}`;
              acc[key] = e;
              return acc;
            },
            {} as { [id: string]: Entry },
            list,
          );

          return {
            id: f.product.id,
            name: f.product.name,
            photo: f.product.photo ? f.product.photo.url : null,
            outlets,
          };
        }),
        R.sortWith<Product>([R.ascend(R.prop('name'))]),
      )(this.entries);

      return result;
    },

    taskDate(): string {
      return this.$route.params.date;
    },
  },

  methods: {
    closeFieldPhoto() {
      this.photo = null;
    },

    closeFieldPhotoDrawer() {
      this.photoGalleryId = null;
    },

    closeMoreInfoDrawer() {
      this.moreInfoId = null;
    },

    showFieldPhoto(url: string) {
      this.photo = url;
    },

    showFieldPhotoDrawer(entryId: string) {
      this.photoGalleryId = entryId;
    },

    showMoreInfoDrawer(args: { entryId: string; outletId: string }) {
      this.moreInfoId = args;
    },

    async update(entry: {
      id: string;
      currPrice: number;
      noteAuditor: string;
      isOos: boolean;
      isPromo: boolean;
    }) {
      const priceInCents = new Decimal(entry.currPrice).times(100).toNumber();

      const presenter = (
        err: Error | null,
        result: EntryEntity | null,
        loading: boolean,
      ) => {
        if (err) {
          this.$message.error(`Unable to update entry: ${err.message}`);
        } else if (result !== null) {
          this.$message.success(`Entry updated.`);
        }
      };

      await this.$coreApp.entry.reviewerUpdate(
        entry.id,
        priceInCents,
        entry.noteAuditor,
        entry.isOos,
        entry.isPromo,
        presenter,
      );

      if (this.tasks) {
        for (let task of this.tasks) {
          const indexToUpdate = R.findIndex(
            R.propEq('id', entry.id),
            task.entries,
          );
          if (indexToUpdate >= 0) {
            task.entries[indexToUpdate].isOos = entry.isOos;
            task.entries[indexToUpdate].noteAuditor = entry.noteAuditor;
            task.entries[indexToUpdate].isPromo = entry.isPromo;
            break;
          }
        }
      }
    },

    async updatePrice(id: string, price: number) {
      // const priceInCents = Math.ceil(price * 100);
      const priceInCents = Math.ceil(new Decimal(price).times(100).toNumber());
      const presenter = (
        err: Error | null,
        result: EntryEntity | null,
        loading: boolean,
      ) => {
        if (err) {
          this.$message.error(`Unable to update price: ${err.message}`);
        } else if (result !== null) {
          this.$message.success(`Price updated.`);
        }
      };
      return await this.$coreApp.entry.updatePrice(id, priceInCents, presenter);
    },

    getStatus(status: any) {
      switch (status) {
        case 'validated':
          return 'price-container validated';
        case 'rejected':
          return 'price-container rejected';

        default:
          return 'price-container';
      }
    },

    async handleApprove() {
      const entryIds = R.map((e) => e.id, this.entries);

      const presenter = (
        err: Error | null,
        result: EntryEntity[] | null,
        loading: boolean,
      ) => {
        this.busy = loading;
        if (err) {
          this.$message.error(`Unable to approve entries: ${err.message}`);
        } else if (result !== null) {
          this.$message.success(`Entries approved.`);

          this.$router.back();
        }
      };

      await this.$coreApp.entry.approve(entryIds, presenter);
    },

    onCheck(e: any) {
      switch (e.target.value) {
        case 'last':
          // tslint:disable-next-line no-bitwise
          this.showPrice = this.showPrice ^ SHOW_PRICES.LAST;
          break;
        case 'promo':
          // tslint:disable-next-line no-bitwise
          this.showPrice = this.showPrice ^ SHOW_PRICES.PROMO;
          break;
        case 'client':
          // tslint:disable-next-line no-bitwise
          this.showPrice = this.showPrice ^ SHOW_PRICES.REF;
          break;
        case 'min':
          // tslint:disable-next-line no-bitwise
          this.showPrice = this.showPrice ^ SHOW_PRICES.MIN;
          break;
        case 'max':
          // tslint:disable-next-line no-bitwise
          this.showPrice = this.showPrice ^ SHOW_PRICES.MAX;
          break;
        default:
          break;
      }
    },

    toggleFullscreen() {
      const doc = document as DocumentWithVendorExts;
      const el = document.documentElement as DocumentElementWithVendorExts;
      if (
        doc.fullscreenEnabled ||
        doc.webkitFullscreenEnabled ||
        doc.mozFullScreenEnabled ||
        doc.msFullscreenEnabled
      ) {
        if (!this.isFullscreen) {
          el.requestFullscreen && el.requestFullscreen();
          el.webkitRequestFullscreen && el.webkitRequestFullscreen();
          el.mozRequestFullScreen && el.mozRequestFullScreen();
          el.msRequestFullscreen && el.msRequestFullscreen();

          this.isFullscreen = true;
        } else {
          doc.exitFullscreen && doc.exitFullscreen();
          doc.webkitExitFullscreen && doc.webkitExitFullscreen();
          doc.mozCancelFullScreen && doc.mozCancelFullScreen();
          doc.msExitFullscreen && doc.msExitFullscreen();

          this.isFullscreen = false;
        }
      }
    },
  },
});
